import { Slot } from "@radix-ui/react-slot";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type * as React from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap border font-heading font-heading-weight uppercase tracking-widest ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-medium focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        solid:
          "border-brand-dark bg-button text-button-text hover:border-brand-darker hover:bg-button-hover hover:text-button-text focus-visible:border-brand-darker focus-visible:bg-button-hover focus-visible:text-button-text",
        outline:
          "border-button bg-inherit bg-transparent text-button hover:border-button-hover hover:bg-transparent hover:text-button-hover focus:border-button-hover focus:bg-transparent focus:text-button-hover",
        ghost:
          "border-none text-button hover:text-button-hover focus:text-button-hover",
        link: "border-none text-button underline-offset-4 hover:text-button-hover hover:underline",
        "solid-error":
          "bg-button-error text-button-text hover:bg-button-error-hover hover:text-button-text",
        "outline-error":
          "border-button-error bg-transparent text-button-error hover:border-button-error-hover hover:bg-transparent hover:text-button-error-hover",
        "ghost-error": "text-button-error hover:text-button-error-hover",
        "link-error":
          "text-button-error underline-offset-4 hover:text-button-error-hover hover:underline",
        "solid-warning":
          "bg-button-warning text-button-text hover:bg-button-warning-hover hover:text-button-text",
        "outline-warning":
          "border-button-warning bg-transparent text-button-warning hover:border-button-warning-hover hover:bg-transparent hover:text-button-warning-hover",
        "ghost-warning": "text-button-warning hover:text-button-warning-hover",
        "link-warning":
          "text-button-warning underline-offset-4 hover:text-button-warning-hover hover:underline",
        "solid-success":
          "bg-button-success text-button-text hover:bg-button-success-hover hover:text-button-text",
        "outline-success":
          "border-button-success bg-transparent text-button-success hover:border-button-success-hover hover:bg-transparent hover:text-button-success-hover",
        "ghost-success": "text-button-success hover:text-button-success-hover",
        "link-success":
          "text-button-success underline-offset-4 hover:text-button-success-hover hover:underline",
      },
      size: {
        sm: "h-8 px-4 py-2 text-sm",
        md: "h-10 px-4 py-2 text-base",
        lg: "h-12 px-4 py-2 text-lg",
        icon: "h-10 w-10 p-4",
      },
    },
    defaultVariants: {
      variant: "solid",
      size: "sm",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  isIconEnd?: boolean;
  isLoading?: boolean;
}

const Button = ({
  className,
  children,
  variant,
  isIconEnd,
  size,
  icon,
  asChild = false,
  isLoading = false,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(
        "flex min-h-8 flex-row gap-2",
        buttonVariants({ variant, size, className })
      )}
      ref={ref}
      {...props}
    >
      {isLoading && <Loader2 className="animate-spin" />}
      {icon && !isIconEnd && (
        <span className="font-heading-weight">{icon}</span>
      )}
      {!(size === "icon") && children}
      {icon && isIconEnd && <span className="font-heading-weight">{icon}</span>}
    </Comp>
  );
};

Button.displayName = "Button";

export { Button, buttonVariants };
